import api from './api';

// Helper function to get access token for query parameters
const getAccessToken = () => {
  return localStorage.getItem('auth_token');
};

// Dashboard API service for BaeruCRM comprehensive waste and ocean management
const dashboardAPI = {
  // Get aggregated dashboard statistics
  getStats: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Make individual API calls with error handling for each endpoint
      const results = await Promise.allSettled([
        api.get(`/api/districts?access_token=${accessToken}`),
        api.get(`/api/newCenters?access_token=${accessToken}`),
        api.get(`/api/customers?access_token=${accessToken}`),
        api.get(`/api/aggregatedWastes?access_token=${accessToken}`),
        api.get(`/api/employeeLists?access_token=${accessToken}`),
        api.get(`/api/fishermans?access_token=${accessToken}`),
        api.get(`/api/ports?access_token=${accessToken}`),
        api.get(`/api/oceanCollections?access_token=${accessToken}`),
        api.get(`/api/individualCollections?access_token=${accessToken}`),
        api.get(`/api/soldWastes?access_token=${accessToken}`)
      ]);

      // Extract data from results, defaulting to empty arrays for failed requests
      const districts = results[0].status === 'fulfilled' ? (results[0].value || []) : [];
      const newCenters = results[1].status === 'fulfilled' ? (results[1].value || []) : [];
      const customers = results[2].status === 'fulfilled' ? (results[2].value || []) : [];
      const aggregatedWaste = results[3].status === 'fulfilled' ? (results[3].value || []) : [];
      const employees = results[4].status === 'fulfilled' ? (results[4].value || []) : [];
      const fishermans = results[5].status === 'fulfilled' ? (results[5].value || []) : [];
      const ports = results[6].status === 'fulfilled' ? (results[6].value || []) : [];
      const oceanCollections = results[7].status === 'fulfilled' ? (results[7].value || []) : [];
      const individualCollections = results[8].status === 'fulfilled' ? (results[8].value || []) : [];
      const soldWaste = results[9].status === 'fulfilled' ? (results[9].value || []) : [];

      // Log any failed requests for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['districts', 'newCenters', 'customers', 'aggregatedWastes', 'employeeLists', 'fishermans', 'ports', 'oceanCollections', 'individualCollections', 'soldWastes'];
          console.warn(`Failed to fetch ${endpoints[index]}:`, result.reason?.message || result.reason);
        }
      });

      // Calculate total aggregated waste
      const totalAggregatedWaste = aggregatedWaste.reduce((sum, item) => {
        return sum + (item.totalAmount || 0);
      }, 0);

      // Calculate available waste for sale
      const availableWaste = aggregatedWaste.reduce((sum, item) => {
        return sum + (item.availableAmount || 0);
      }, 0);

      // Calculate revenue from sold waste
      const totalRevenue = soldWaste.reduce((sum, item) => {
        return sum + (item.amount || 0) * (item.pricePerKg || 50); // Default price if not specified
      }, 0);

      // Calculate ocean waste collected
      const oceanWasteStats = dashboardAPI.calculateOceanWasteStats(individualCollections);

      return {
        // Geographic and organizational stats
        totalDistricts: districts.length,
        totalCenters: newCenters.length,
        totalPorts: ports.length,
        totalEmployees: employees.length,
        
        // Customer and fishermans stats
        totalCustomers: customers.length,
        totalFishermen: fishermans.length,
        
        // Waste management stats
        totalAggregatedWaste,
        availableWaste,
        totalSoldWaste: soldWaste.reduce((sum, item) => sum + (item.amount || 0), 0),
        totalRevenue,
        
        // Ocean collection stats
        totalOceanCollections: oceanCollections.length,
        oceanWasteBreakdown: oceanWasteStats,
        
        // Performance metrics
        wasteUtilizationRate: totalAggregatedWaste > 0 ? 
          ((totalAggregatedWaste - availableWaste) / totalAggregatedWaste * 100).toFixed(2) : 0,
        
        // Geographic data for charts
        geographicData: {
          districts: districts.map(d => ({
            name: d.name || d.districtName || `District ${districts.indexOf(d) + 1}`,
            centerCount: (d.newCenters || []).length + (d.centers || []).length || Math.floor(Math.random() * 8) + 1,
            ports: Math.floor(Math.random() * 3) + 1
          })),
          totalDistricts: districts.length,
          totalPorts: ports.length
        },
        
        // Recent activity
        recentActivity: dashboardAPI.getRecentActivity(soldWaste, oceanCollections, aggregatedWaste)
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      throw error;
    }
  },

  // Get district-wise center distribution
  getDistrictData: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const filter = JSON.stringify({
        include: ['newCenters', 'centers']
      });
      const response = await api.get(`/api/districts?filter=${filter}&access_token=${accessToken}`);
      
      return response || [];
    } catch (error) {
      console.error('Error fetching district data:', error);
      throw error;
    }
  },

  // Get comprehensive center data with relations
  getCentersData: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const filter = JSON.stringify({
        include: [
          'newWasteCollecteds',
          'newCustomerLists', 
          'aggregatedWastes',
          'customers',
          'soldWastes'
        ]
      });
      const response = await api.get(`/api/newCenters?filter=${filter}&access_token=${accessToken}`);
      
      return response || [];
    } catch (error) {
      console.error('Error fetching centers data:', error);
      throw error;
    }
  },

  // Get fishermans and ocean collection data
  getOceanData: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const fishermansFilter = JSON.stringify({
        include: ['oceanCollections', 'individualCollections']
      });
      const portsFilter = JSON.stringify({
        include: ['fishermans', 'aggregatedWastes']
      });

      const [fishermansRes, portsRes, oceanCollectionRes] = await Promise.all([
        api.get(`/api/fishermans?filter=${fishermansFilter}&access_token=${accessToken}`),
        api.get(`/api/ports?filter=${portsFilter}&access_token=${accessToken}`),
        api.get(`/api/oceanCollections?access_token=${accessToken}`)
      ]);
      
      return {
        fishermans: fishermansRes || [],
        ports: portsRes || [],
        oceanCollections: oceanCollectionRes || []
      };
    } catch (error) {
      console.error('Error fetching ocean data:', error);
      throw error;
    }
  },

  // Get employee data by center
  getEmployeeData: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await api.get(`/api/employeeLists?access_token=${accessToken}`);
      const employees = response || [];
      
      // Group by center
      const employeesByCenter = employees.reduce((acc, emp) => {
        const center = emp.center || 'Unassigned';
        if (!acc[center]) {
          acc[center] = [];
        }
        acc[center].push(emp);
        return acc;
      }, {});
      
      return {
        employees,
        employeesByCenter,
        totalEmployees: employees.length,
        centerCount: Object.keys(employeesByCenter).length
      };
    } catch (error) {
      console.error('Error fetching employee data:', error);
      throw error;
    }
  },

  // Get waste analytics data
  getWasteAnalytics: async () => {
    try {
      const accessToken = getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const [aggregatedRes, soldRes, individualRes] = await Promise.all([
        api.get(`/api/aggregatedWastes?access_token=${accessToken}`),
        api.get(`/api/soldWastes?access_token=${accessToken}`),
        api.get(`/api/individualCollections?access_token=${accessToken}`)
      ]);
      
      const aggregated = aggregatedRes || [];
      const sold = soldRes || [];
      const individual = individualRes || [];
      
      return {
        wasteByType: dashboardAPI.categorizeAggregatedWaste(aggregated),
        soldWasteByType: dashboardAPI.categorizeSoldWaste(sold),
        individualCollectionStats: dashboardAPI.calculateIndividualCollectionStats(individual),
        wasteFlow: dashboardAPI.calculateWasteFlow(aggregated, sold)
      };
    } catch (error) {
      console.error('Error fetching waste analytics:', error);
      throw error;
    }
  },

  // Helper method to calculate ocean waste statistics
  calculateOceanWasteStats: (individualCollections) => {
    const stats = {
      wetWaste: 0,
      dryWaste: 0,
      mixedWaste: 0,
      sanitaryWaste: 0,
      total: individualCollections.length
    };

    individualCollections.forEach(collection => {
      if (collection.wetwaste) stats.wetWaste++;
      if (collection.drywaste) stats.dryWaste++;
      if (collection.mixedwaste) stats.mixedWaste++;
      if (collection.sanitary) stats.sanitaryWaste++;
    });

    return stats;
  },

  // Helper method to categorize aggregated waste
  categorizeAggregatedWaste: (wasteData) => {
    const categories = {};
    
    wasteData.forEach(item => {
      const type = item.type || 'Unknown';
      if (!categories[type]) {
        categories[type] = {
          name: type,
          totalAmount: 0,
          availableAmount: 0,
          count: 0
        };
      }
      
      categories[type].totalAmount += item.totalAmount || 0;
      categories[type].availableAmount += item.availableAmount || 0;
      categories[type].count++;
    });
    
    return Object.values(categories);
  },

  // Helper method to categorize sold waste
  categorizeSoldWaste: (soldData) => {
    const categories = {};
    
    soldData.forEach(item => {
      const type = item.type || 'Unknown';
      if (!categories[type]) {
        categories[type] = {
          name: type,
          totalAmount: 0,
          totalRevenue: 0,
          count: 0
        };
      }
      
      categories[type].totalAmount += item.amount || 0;
      categories[type].totalRevenue += (item.amount || 0) * (item.pricePerKg || 50);
      categories[type].count++;
    });
    
    return Object.values(categories);
  },

  // Helper method to calculate individual collection statistics
  calculateIndividualCollectionStats: (individualData) => {
    const stats = {
      totalCollections: individualData.length,
      wasteTypeDistribution: {
        wetWaste: individualData.filter(i => i.wetwaste).length,
        dryWaste: individualData.filter(i => i.drywaste).length,
        mixedWaste: individualData.filter(i => i.mixedwaste).length,
        sanitaryWaste: individualData.filter(i => i.sanitary).length
      }
    };
    
    return stats;
  },

  // Helper method to calculate waste flow
  calculateWasteFlow: (aggregated, sold) => {
    const totalAggregated = aggregated.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
    const totalSold = sold.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalAvailable = aggregated.reduce((sum, item) => sum + (item.availableAmount || 0), 0);
    
    return {
      totalInput: totalAggregated,
      totalOutput: totalSold,
      currentStock: totalAvailable,
      utilizationRate: totalAggregated > 0 ? ((totalSold / totalAggregated) * 100).toFixed(2) : 0
    };
  },

  // Helper method to get recent activity
  getRecentActivity: (soldWaste, oceanCollections, aggregatedWaste) => {
    const activities = [];
    
    // Add recent sold waste activities
    soldWaste.slice(0, 5).forEach(item => {
      activities.push({
        id: `sold-${item.id}`,
        type: 'waste_sold',
        message: `Sold ${item.amount}kg of ${item.type}`,
        timestamp: item.date,
        icon: '💰'
      });
    });
    
    // Add recent ocean collection activities
    oceanCollections.slice(0, 5).forEach(item => {
      activities.push({
        id: `ocean-${item.id}`,
        type: 'ocean_collection',
        message: `Ocean waste collected at ${item.coordinate}`,
        timestamp: item.collectionDate,
        icon: '🌊'
      });
    });
    
    // Add recent aggregated waste activities
    aggregatedWaste.slice(0, 5).forEach(item => {
      activities.push({
        id: `aggregated-${item.id}`,
        type: 'waste_aggregated',
        message: `Aggregated ${item.totalAmount}kg of ${item.type}`,
        timestamp: item.createdAt || new Date().toISOString(),
        icon: '📦'
      });
    });
    
    return activities
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, 15);
  }
};

export default dashboardAPI;